import React, { forwardRef, useState } from 'react';
import { Check, X, ChevronDown, ChevronUp, Edit, Plus, Trash2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { QAgentSuggestion } from '../../../services/QAgent';
import { cn } from '@/lib/utils';

interface ParsedParagraph {
  index: number;
  text: string;
  html: string;
}

interface DocumentParagraphProps {
  paragraph: ParsedParagraph;
  suggestions: QAgentSuggestion[];
  onAccept: (suggestionId: string) => void;
  onReject: (suggestionId: string) => void;
  disabled?: boolean;
}

const getTypeIcon = (type: QAgentSuggestion['type']) => {
  switch (type) {
    case 'edit':
      return <Edit className="h-3 w-3" />;
    case 'insert':
      return <Plus className="h-3 w-3" />;
    case 'delete':
      return <Trash2 className="h-3 w-3" />;
  }
};

const getTypeColor = (type: QAgentSuggestion['type']) => {
  switch (type) {
    case 'edit':
      return 'border-blue-300 bg-blue-50';
    case 'insert':
      return 'border-green-300 bg-green-50';
    case 'delete':
      return 'border-red-300 bg-red-50';
  }
};

const getTypeBadgeColor = (type: QAgentSuggestion['type']) => {
  switch (type) {
    case 'edit':
      return 'bg-blue-100 text-blue-700 border-blue-200';
    case 'insert':
      return 'bg-green-100 text-green-700 border-green-200';
    case 'delete':
      return 'bg-red-100 text-red-700 border-red-200';
  }
};

export const DocumentParagraph = forwardRef<HTMLDivElement, DocumentParagraphProps>(
  ({ paragraph, suggestions, onAccept, onReject, disabled = false }, ref) => {
    const [expandedSuggestions, setExpandedSuggestions] = useState<Set<string>>(new Set());

    const pendingSuggestions = suggestions.filter(s => s.status === 'pending');
    const hasPendingSuggestions = pendingSuggestions.length > 0;

    const toggleSuggestion = (id: string) => {
      setExpandedSuggestions(prev => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
    };

    // If no pending suggestions, render the paragraph normally
    if (pendingSuggestions.length === 0) {
      return (
        <div
          ref={ref}
          className="prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: paragraph.html }}
        />
      );
    }

    return (
      <div
        ref={ref}
        className={cn(
          "relative rounded-lg transition-colors",
          hasPendingSuggestions && "border-l-4 border-l-brand pl-3 -ml-3"
        )}
      >
        {/* Original paragraph text */}
        <div
          className="prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: paragraph.html }}
        />

        {/* Suggestions overlay - only show pending suggestions */}
        {pendingSuggestions.map((suggestion) => {
          const isExpanded = expandedSuggestions.has(suggestion.id);

          return (
            <div
              key={suggestion.id}
              className={cn(
                "mt-2 rounded-lg border-2 transition-all",
                getTypeColor(suggestion.type),
              )}
            >
              {/* Suggestion header - clickable to expand */}
              <button
                onClick={() => toggleSuggestion(suggestion.id)}
                className="w-full flex items-center justify-between p-2 text-left hover:bg-white/50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Badge
                    variant="outline"
                    className={cn("flex items-center gap-1 text-xs", getTypeBadgeColor(suggestion.type))}
                  >
                    {getTypeIcon(suggestion.type)}
                    <span className="capitalize">{suggestion.type}</span>
                  </Badge>
                </div>
                {isExpanded ? (
                  <ChevronUp className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                )}
              </button>

              {/* Expanded content */}
              {isExpanded && (
                <div className="px-3 pb-3 space-y-2">
                  {/* Diff visualization */}
                  <div className="text-sm space-y-1">
                    {suggestion.type === 'edit' && (
                      <>
                        {suggestion.oldText && (
                          <div className="bg-red-100 border border-red-200 rounded px-2 py-1">
                            <span className="text-red-700 line-through">{suggestion.oldText}</span>
                          </div>
                        )}
                        {suggestion.newText && (
                          <div className="bg-green-100 border border-green-200 rounded px-2 py-1">
                            <span className="text-green-700">{suggestion.newText}</span>
                          </div>
                        )}
                      </>
                    )}

                    {suggestion.type === 'insert' && suggestion.newText && (
                      <div className="bg-green-100 border border-green-200 rounded px-2 py-1">
                        <span className="text-green-700">+ {suggestion.newText}</span>
                      </div>
                    )}

                    {suggestion.type === 'delete' && suggestion.oldText && (
                      <div className="bg-red-100 border border-red-200 rounded px-2 py-1">
                        <span className="text-red-700 line-through">{suggestion.oldText}</span>
                      </div>
                    )}
                  </div>

                  {/* Comment */}
                  {suggestion.comment && (
                    <div className="flex items-start gap-2 text-xs text-muted-foreground bg-white/70 rounded px-2 py-1.5">
                      <span className="text-sm">💡</span>
                      <span>{suggestion.comment}</span>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-2 pt-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onAccept(suggestion.id);
                      }}
                      disabled={disabled}
                      className="h-7 gap-1.5 text-green-600 border-green-200 hover:bg-green-50 bg-white"
                    >
                      <Check className="h-3.5 w-3.5" />
                      Accept
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onReject(suggestion.id);
                      }}
                      disabled={disabled}
                      className="h-7 gap-1.5 text-red-600 border-red-200 hover:bg-red-50 bg-white"
                    >
                      <X className="h-3.5 w-3.5" />
                      Reject
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }
);

DocumentParagraph.displayName = 'DocumentParagraph';

export default DocumentParagraph;
